import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Search } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setShowSearch(false);
  }, [location]);

  const navLinks = [
    { path: '/home', label: 'Home' },
    { path: '/sobre', label: 'Sobre' },
    { path: '/servicos', label: 'Serviços' },
    { path: '/smartaudit', label: 'SmartAudit' },
    { path: '/blog', label: 'Blog' },
    { path: '/downloads', label: 'Downloads' },
    { path: '/contato', label: 'Contato' },
  ];

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-lg' 
          : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <nav className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/home" className="block flex-shrink-0 group" data-testid="logo-link">
            <motion.div 
              className="flex items-center gap-3"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
              whileHover={{ scale: 1.02 }}
            >
              {/* Shield Icon SVG with animations */}
              <motion.svg 
                viewBox="0 0 100 120" 
                className="h-12 md:h-14 w-auto"
                whileHover={{ 
                  scale: 1.1,
                  rotate: [0, -5, 5, 0],
                  transition: { duration: 0.5 }
                }}
              >
                {/* Shield base - dark teal */}
                <motion.path 
                  d="M50 5 L90 20 L90 55 C90 85 50 115 50 115 C50 115 10 85 10 55 L10 20 Z" 
                  fill="#1a4a5e"
                  stroke="#1a4a5e"
                  strokeWidth="2"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.2 }}
                />
                {/* Shield highlight - lighter teal */}
                <path 
                  d="M50 5 L50 115 C50 115 10 85 10 55 L10 20 Z" 
                  fill="#2a6a7e"
                />
                {/* Inner circuit pattern - green accent with glow effect */}
                <motion.path 
                  d="M35 35 L35 55 L50 55" 
                  fill="none" 
                  stroke="#10b981" 
                  strokeWidth="4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                  className="group-hover:drop-shadow-[0_0_8px_rgba(16,185,129,0.8)]"
                />
                <motion.path 
                  d="M50 35 L50 75" 
                  fill="none" 
                  stroke="#5ec4a8" 
                  strokeWidth="4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.7 }}
                  className="group-hover:drop-shadow-[0_0_8px_rgba(94,196,168,0.8)]"
                />
                <motion.path 
                  d="M65 45 L65 65 L50 65" 
                  fill="none" 
                  stroke="#10b981" 
                  strokeWidth="4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.9 }}
                  className="group-hover:drop-shadow-[0_0_8px_rgba(16,185,129,0.8)]"
                />
                {/* Decorative dots with pulse animation */}
                <motion.circle 
                  cx="35" cy="35" r="3" fill="#10b981"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3, delay: 1.1 }}
                  className="group-hover:animate-pulse"
                />
                <motion.circle 
                  cx="65" cy="45" r="3" fill="#5ec4a8"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3, delay: 1.2 }}
                  className="group-hover:animate-pulse"
                />
                <motion.circle 
                  cx="50" cy="75" r="3" fill="#10b981"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.3, delay: 1.3 }}
                  className="group-hover:animate-pulse"
                />
              </motion.svg>
              
              {/* Text Logo */}
              <span className="text-2xl md:text-3xl font-bold tracking-tight">
                <motion.span 
                  className="text-[#1a4a5e] inline-block"
                  whileHover={{ color: '#10b981' }}
                  transition={{ duration: 0.3 }}
                >
                  DMM
                </motion.span>
                <span className="text-[#1a4a5e] font-normal ml-1 group-hover:text-[#2a6a7e] transition-colors duration-300">Security</span>
              </span>
            </motion.div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link, index) => (
              <motion.div
                key={link.path}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <Link
                  to={link.path}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 rounded-lg ${
                    location.pathname === link.path
                      ? 'text-[#10b981]'
                      : 'text-[#1a4a5e] hover:text-[#10b981]'
                  }`}
                >
                  {link.label}
                  {location.pathname === link.path && (
                    <motion.div
                      layoutId="activeNav"
                      className="absolute bottom-0 left-1/2 -translate-x-1/2 w-6 h-0.5 bg-[#10b981] rounded-full"
                    />
                  )}
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Right Side - Search, Phone, CTA */}
          <div className="hidden lg:flex items-center gap-4">
            {/* Search Button */}
            <button
              onClick={() => setShowSearch(!showSearch)}
              className="p-2 text-[#1a4a5e] hover:text-[#10b981] hover:bg-gray-100 rounded-full transition-all"
              aria-label="Buscar"
              data-testid="search-toggle"
            >
              <Search className="w-5 h-5" />
            </button>

            {/* Phone */}
            <a
              href="tel:+551143266707"
              className="flex items-center gap-2 text-[#1a4a5e] hover:text-[#10b981] transition-colors text-sm font-medium"
              data-testid="phone-link"
            >
              <Phone className="w-4 h-4" />
              <span>(11) 4326-6707</span>
            </a>

            {/* CTA Button */}
            <Link
              to="/contato"
              data-testid="cta-contact-btn"
              className="bg-[#10b981] text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-[#0ea573] transition-all duration-300 hover:shadow-lg hover:shadow-[#10b981]/25 hover:scale-105"
            >
              Fale Conosco
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-[#1a4a5e] p-2 hover:bg-gray-100 rounded-lg transition-colors z-50"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Search Bar */}
        <AnimatePresence>
          {showSearch && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden border-t border-gray-100"
            >
              <div className="py-4">
                <div className="relative max-w-xl mx-auto">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar no site..."
                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#10b981] focus:border-transparent transition-all text-[#1a4a5e]"
                    autoFocus
                  />
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="lg:hidden overflow-hidden border-t border-gray-100"
              data-testid="mobile-menu"
            >
              <div className="pt-4 pb-6 space-y-2">
                {navLinks.map((link, index) => (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={link.path}
                      className={`block px-4 py-3 rounded-xl font-medium transition-all ${
                        location.pathname === link.path
                          ? 'bg-[#10b981] text-white'
                          : 'text-[#1a4a5e] hover:bg-gray-100'
                      }`}
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navLinks.length * 0.05 }}
                  className="pt-4 border-t border-gray-100"
                >
                  <a
                    href="tel:+551143266707"
                    className="flex items-center gap-3 px-4 py-3 text-[#1a4a5e] hover:text-[#10b981] transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    <span>(11) 4326-6707</span>
                  </a>
                  <Link
                    to="/contato"
                    className="block mt-3 mx-4 bg-[#10b981] text-white text-center px-6 py-3 rounded-xl font-semibold hover:bg-[#0ea573] transition-all"
                  >
                    Fale Conosco
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;
